import type { ApiClient } from "@shell/auth/apiClient";
import { ApiResponseError } from "@shell/auth/apiClient";
import {
    addAssetToUserResponseSchema,
    userDirectoryEntrySchema,
    userSearchResponseSchema,
    type AddAssetToUserPayload,
    type AddAssetToUserResult,
    type UserDirectoryEntry,
} from "./types";

export async function searchUsers(
    client: ApiClient,
    q: string,
): Promise<readonly UserDirectoryEntry[]> {
    const trimmed = q.trim();
    if (trimmed.length < 2) return [];

    const params = new URLSearchParams({ q: trimmed });
    const raw = await client.getJson<unknown>(`/api/users/search?${params.toString()}`);
    return userSearchResponseSchema.parse(raw).results;
}

/**
 * Resolves a single HR user id to its directory entry. Returns null when
 * the HR directory has no record for the id (the SPA treats this as a
 * benign "unknown / departed" state and falls back to displaying the raw
 * id wherever the entry would have appeared).
 *
 * Empty / whitespace ids short-circuit to null without touching the
 * network — useful when callers feed in a column that's optionally
 * populated (e.g. `assignedToUserId` on workspace-target assignments).
 */
export async function lookupUser(
    client: ApiClient,
    id: string,
): Promise<UserDirectoryEntry | null> {
    const trimmed = id.trim();
    if (trimmed === "") return null;

    try {
        const raw = await client.getJson<unknown>(
            `/api/users/${encodeURIComponent(trimmed)}`,
        );
        return userDirectoryEntrySchema.parse(raw);
    } catch (error) {
        if (error instanceof ApiResponseError && error.status === 404) {
            return null;
        }
        throw error;
    }
}

/**
 * User-anchored "add asset" call. Hits the atomic backend endpoint that
 * creates the asset and the user assignment in a single Cosmos batch —
 * see `AddAssetToUserEndpoint` on the API.
 *
 * The caller supplies BOTH ids (asset + assignment) so the operation is
 * idempotent: a phone losing connectivity mid-save can replay without
 * risking a duplicate. A 409 from the server therefore means the prior
 * call already succeeded; the SPA treats that as success after refetching.
 */
export async function addAssetToUser(
    client: ApiClient,
    userId: string,
    payload: AddAssetToUserPayload,
): Promise<AddAssetToUserResult> {
    // The route id is duplicated into the body deliberately. The backend
    // DTO marks `UserId` as `required`; System.Text.Json enforces that
    // on body deserialization *before* FastEndpoints' route-value
    // binding has a chance to populate it, so omitting it here yields
    // a 400 "missing required properties: userId" even though the value
    // is sitting right there in the URL. Sending both keeps STJ happy;
    // route binding still wins on the server.
    const response = await client.fetch(
        `/api/users/${encodeURIComponent(userId)}/assets`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                userId,
                asset: payload.asset,
                assignmentId: payload.assignmentId,
            }),
        },
    );

    if (!response.ok) {
        const body = await response.text();
        throw new ApiResponseError(response.status, response.statusText, body);
    }

    const raw = (await response.json()) as unknown;
    return addAssetToUserResponseSchema.parse(raw);
}
